import { PRIVATEPATHS } from '@core/enums/paths.enum';
import { IAs2Partner } from '@core/models/as2-message.model';
import {
    AnimatedListSkeleton,
    AnimatedTable,
    AnimatedTableBody,
    AnimatedTableRow,
} from 'components/animations';
import BreadCrumb from 'Common/BreadCrumb';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import as2IntegrationService from 'services/api/as2-integration.service';
import { ConnectionTestResult, formatConnectionTestShort } from '../Integrations/integration-user-messages';
import As2PartnerQuickActions from 'components/integrations/As2PartnerQuickActions';
import As2EpcisDispatchPanel from './As2EpcisDispatchPanel';
import As2PartnerModal from './As2PartnerModal';
import As2PartnersGuide from './As2PartnersGuide';
import As2SetupGuide from './As2SetupGuide';
import IntegrationWizard from '../Integrations/IntegrationWizard';
import { directionBadgeClass, directionLabel } from './as2-partner.utils';
import As2SectionNav from './As2SectionNav';

const As2Partners: FC = () => {
    const [partners, setPartners] = useState<IAs2Partner[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<IAs2Partner | null>(null);
    const [testResults, setTestResults] = useState<Record<string, ConnectionTestResult>>({});
    const [wizardOpen, setWizardOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r: any = await as2IntegrationService.listPartners();
            setPartners(r.data.as2Partners || []);
        } catch {
            /* interceptor */
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <>
            <BreadCrumb pageTitle="AS2 Partners" />
            <section className="py-5">
                <div className="admin-motion-card flex flex-col gap-4 bg-white p-3">
                    <As2SectionNav />

                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <h3 className="font-extrabold text-[#bd902e]">Trading partners</h3>
                            <p className="mt-1 text-sm text-slate-500">
                                Manage production and pre-go-live AS2 partners. Expand a row for full configuration.
                            </p>
                        </div>
                        <ButtonBase innerText="Add AS2 partner" onClick={() => setWizardOpen(true)} />
                    </div>

                    <As2SetupGuide />
                    <As2PartnersGuide />

                    <As2EpcisDispatchPanel partners={partners} environmentFilter="ALL" compact />

                    {loading ? (
                        <AnimatedListSkeleton rows={5} />
                    ) : (
                        <div className="w-full min-w-0 overflow-x-auto">
                            <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                <thead>
                                    <tr className="rounded-md bg-slate-50">
                                        <th className="px-3.5 py-2.5 text-xs font-semibold uppercase text-slate-500">Code</th>
                                        <th className="px-3.5 py-2.5 text-xs font-semibold uppercase text-slate-500">Direction</th>
                                        <th className="px-3.5 py-2.5 text-xs font-semibold uppercase text-slate-500">Env</th>
                                        <th className="px-3.5 py-2.5 text-xs font-semibold uppercase text-slate-500">Name</th>
                                        <th className="px-3.5 py-2.5 text-xs font-semibold uppercase text-slate-500">AS2 ID</th>
                                        <th className="px-3.5 py-2.5 text-xs font-semibold uppercase text-slate-500">Receive URL</th>
                                        <th className="px-3.5 py-2.5 text-xs font-semibold uppercase text-slate-500">Enabled</th>
                                        <th className="px-3.5 py-2.5 text-xs font-semibold uppercase text-slate-500">Last test</th>
                                        <th className="px-3.5 py-2.5 text-xs font-semibold uppercase text-slate-500">Actions</th>
                                    </tr>
                                </thead>
                                <AnimatedTableBody loading={loading} itemCount={partners.length} animationKey="as2-partners">
                                    {partners.map((p) => {
                                        const test = testResults[p.code!];
                                        const short = test ? formatConnectionTestShort(test) : null;
                                        return (
                                            <AnimatedTableRow
                                                key={p.code}
                                                rowKey={p.code}
                                                detailRecord={p}
                                                className={`rounded-md bg-slate-50 hover:bg-[#bd902e10] ${p.enabled === false ? 'opacity-60' : ''}`}
                                            >
                                                <td className="px-3.5 py-2.5 text-sm font-semibold">{p.code}</td>
                                                <td className="px-3.5 py-2.5">
                                                    <span className={`rounded px-2 py-0.5 text-xs font-medium ${directionBadgeClass(p.direction)}`}>
                                                        {directionLabel(p.direction)}
                                                    </span>
                                                </td>
                                                <td className="px-3.5 py-2.5">
                                                    <span
                                                        className={`rounded px-2 py-0.5 text-xs font-medium ${
                                                            p.environment === 'TEST'
                                                                ? 'bg-blue-100 text-blue-800'
                                                                : 'bg-purple-100 text-purple-800'
                                                        }`}
                                                    >
                                                        {p.environment || 'PROD'}
                                                    </span>
                                                </td>
                                                <td className="px-3.5 py-2.5 text-sm">{p.name}</td>
                                                <td className="px-3.5 py-2.5 text-xs font-mono">{p.as2Id}</td>
                                                <td className="max-w-[220px] px-3.5 py-2.5 text-xs" title={p.as2Url}>
                                                    {p.as2Url ? (
                                                        <span className="row-detail-panel__value--truncate block">
                                                            {p.as2Url}
                                                        </span>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                                <td className="px-3.5 py-2.5">
                                                    <span
                                                        className={`rounded px-2 py-0.5 text-xs font-medium ${
                                                            p.enabled
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-slate-100 text-slate-500'
                                                        }`}
                                                    >
                                                        {p.enabled ? 'Yes' : 'No'}
                                                    </span>
                                                </td>
                                                <td className="px-3.5 py-2.5 text-xs">
                                                    {short ? (
                                                        <span
                                                            className={short.ok ? 'text-green-700' : 'text-red-600'}
                                                            title={short.title}
                                                        >
                                                            {short.label}
                                                        </span>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                                <td className="px-3.5 py-2.5">
                                                    <As2PartnerQuickActions
                                                        partner={p}
                                                        onPartnerUpdated={load}
                                                        onTestComplete={(code, result) =>
                                                            setTestResults((prev) => ({
                                                                ...prev,
                                                                [code]: result,
                                                            }))
                                                        }
                                                        onEdit={(partner) => {
                                                            setEditing(partner);
                                                            setModalOpen(true);
                                                        }}
                                                        compact
                                                    />
                                                </td>
                                            </AnimatedTableRow>
                                        );
                                    })}
                                </AnimatedTableBody>
                            </AnimatedTable>
                            {partners.length === 0 && (
                                <p className="py-8 text-center text-sm text-slate-500">
                                    No AS2 partners yet. Add one from{' '}
                                    <Link to={PRIVATEPATHS.Integrations} className="text-[#bd902e] hover:underline">
                                        Setup &amp; health
                                    </Link>
                                    .
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </section>

            <As2PartnerModal
                open={modalOpen}
                existing={editing}
                onClose={() => setModalOpen(false)}
                onSaved={load}
            />
            <IntegrationWizard
                open={wizardOpen}
                onClose={() => setWizardOpen(false)}
                onComplete={load}
            />
        </>
    );
};

export default As2Partners;
